import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Checkbox, Dropdown, Input, Menu, notification, Table } from 'antd';
import Fuse from 'fuse.js';
import { downArrowNew, leftScrollButton, rightScrollButton, searchIcon } from './common/Icons';
import moment from 'moment';
import {
    Column,
    Dose,
    IVaccineStatus,
    VACCINATION_STATUS,
    VaccineInfo,
    VaccineInfoAPIResponse,
    diseaseIdToName,
} from '../api/fetchVaccineSchedule';
import VaccineSelectionModal, { Status, buttons } from './VaccineSelectionModal';
import DoseCardWrapper from './DoseCard';
import { MHCWithVaccination } from '../store/types/medicalHistory';
import { useDispatch, useSelector } from 'react-redux';
import { getPrescription } from '../store/selectors/prescriptionPad';
import {
    getDefaultClinicId,
    getDefaultTemplateV2,
    getPatientByLocalId,
    getTemplateV2,
    getTemplates,
} from '../store/idbHandler';
import PadBottomStrip from './prescriptionPad/rightPanel/workspace/padV2/VaccinationBottomStrip';
import { getAgeInYears, getAgeString, sideScroll } from '../store/utils';
import { GO_TO_MY_RX_TEMPLATE } from '../routes/paths';
import { useHistory } from 'react-router-dom';
import { clearAdministeredVaccines } from '../store/actions/prescriptionPad';
import pushPtMedHistory from '../api/pushPtMedHistory';
import { AP } from '../pages/Appointments';
import pushPtVaccHistory from '../api/pushPtVaccHistory';
import { Age } from '../store/types/patient';

export const fuseConfig = {
    keys: ['_category'],
    threshold: 0.1,
    ignoreLocation: true,
    useExtendedSearch: true,
    shouldSort: false,
};

const doseBoxWidth = 200;

const formVaccineData = (
    groupedDataByCategory: VaccineInfoAPIResponse,
    categoryKeys: {
        allKeys: string[];
        currKeys: string[];
    },
    ageGroups: (Column & { checked?: boolean })[],
    selectedVaccines: Dose[] | undefined,
    setSelectedVaccines: (vac: Dose[]) => void,
    setShowModal: (val: {
        isVisible: boolean;
        status?: VACCINATION_STATUS.GIVEN | VACCINATION_STATUS.DUE;
        date?: string;
    }) => void,
    administedVaccines: IVaccineStatus[],
    currSch: 'plan' | 'otherVaccs',
    dob: moment.Moment,
    today: moment.Moment,
) => {
    const finalVals = [];
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const plan = groupedDataByCategory[currSch];

    for (let i = 0; i < categoryKeys.currKeys.length; i++) {
        const currKey = categoryKeys.currKeys[i];
        const ageG = {};

        for (let j = 0; j < ageGroups.length; j++) {
            const currAgeGroup = ageGroups[j];

            if (plan && currAgeGroup.ageGroupName) {
                const schedule = plan[currKey].vaccineSchedule[currAgeGroup.ageGroupName];

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                ageG[currAgeGroup.ageGroupName] = (
                    <DoseCardWrapper
                        currVal={schedule}
                        hrsFromBirth={currAgeGroup?.minAgeFromBirth}
                        setSelectedVaccines={setSelectedVaccines}
                        selectedVaccines={selectedVaccines}
                        setShowModal={setShowModal}
                        administedVaccines={administedVaccines}
                        dob={dob}
                        today={today}
                        currSch={currSch}
                    />
                );
            }
        }

        const currDisease = diseaseIdToName[currKey as keyof typeof diseaseIdToName];

        if (currDisease) {
            const caughtUpMin =
                currDisease.minAge?.value === undefined
                    ? undefined
                    : currDisease.minAge?.value === 0
                    ? 'Birth'
                    : [currDisease.minAge?.value, currDisease.minAge?.unit].join(' ').trim();
            const caughtUpMax =
                currDisease.maxAge?.value === undefined
                    ? undefined
                    : [currDisease.maxAge?.value, currDisease.maxAge?.unit].join(' ').trim();

            const diseaseName = (
                <p
                    className={`font-700 h-full w-full flex items-center gap-8 p-32 text-14`}
                    style={{
                        boxShadow: '0px 0px 10px #D1D1D1',
                        clipPath: 'inset(0px -15px 0px 0px)',
                    }}
                >
                    {currDisease.name}
                </p>
            );

            const final = {
                key: i,
                Disease: diseaseName,
                ...ageG,
            };

            finalVals.push(final);
        }
    }

    finalVals.push({ key: categoryKeys.currKeys.length, Disease: '' });

    return { ageGroups, finalVals };
};

const VaccinationChart: React.FC<{
    vaccinationData?: Partial<VaccineInfo>;
    vaccinationHistory?: IVaccineStatus[];
    clearSelection?: () => void;
    patientId?: string | null;
    clinic_id: string;
    onSave?: (setLoading: (loading: boolean) => void) => void;
    onChange?: (newVaccs: IVaccineStatus[], allVaccs: IVaccineStatus[]) => void;
    onDueFollowupSet?: (followupDate: IVaccineStatus) => void;
    defaultSchedule?: 'plan' | 'otherVaccs';
}> = (props) => {
    const {
        clearSelection,
        vaccinationHistory,
        vaccinationData,
        patientId,
        clinic_id,
        onSave,
        onChange,
        onDueFollowupSet,
        defaultSchedule,
    } = props;

    const [age, setAge] = useState<Age | undefined>();

    const [patientDob, setPatientDob] = useState<string>();
    const [showModal, setShowModal] = useState<{
        isVisible: boolean;
        status?: Status;
        date?: string;
        title?: string;
        isBlockStatus?: boolean;
    }>({
        isVisible: false,
        status: VACCINATION_STATUS.GIVEN,
        isBlockStatus: false,
    });
    const [vaccineData, setVaccineData] = useState<VaccineInfoAPIResponse>(vaccinationData ?? {});
    const [currSchedule, setCurrSchedule] = useState<'plan' | 'otherVaccs'>(defaultSchedule || 'plan');
    const [ageGroups, setAgeGroups] = useState<(Column & { checked?: boolean })[]>(
        vaccinationData?.columns ? vaccinationData.columns[currSchedule] : [],
    );

    const tableRefPlan = useRef<any>();
    const tableRefOther = useRef<any>();
    const tableRefCurr = currSchedule === 'otherVaccs' ? tableRefOther : tableRefPlan;
    const [selectedVaccines, setSelectedVaccines] = useState<Dose[]>();
    const [administedVaccines, setAdministedVaccines] = useState<IVaccineStatus[]>(
        vaccinationHistory ?? [],
    );

    const [isSaving, setIsSaving] = useState(false);

    const dob = moment(patientDob);

    const today = moment();
    const hoursFromBirth = today.diff(dob, 'hours');

    useEffect(() => {
        if (vaccinationHistory) {
            setAdministedVaccines(vaccinationHistory ?? []);
        }
    }, [vaccinationHistory]);

    const [categoryKeys, setCategoryKeys] = useState<{
        allKeys: string[];
        currKeys: string[];
    }>({
        allKeys:
            Object.keys(
                vaccinationData
                    ? vaccinationData[currSchedule]
                        ? vaccinationData[currSchedule]
                        : {}
                    : {},
            ) ?? [],
        currKeys:
            Object.keys(
                vaccinationData
                    ? vaccinationData[currSchedule]
                        ? vaccinationData[currSchedule]
                        : {}
                    : {},
            ) ?? [],
    });

    const history = useHistory();
    const dispatch = useDispatch();

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const currPlan = vaccineData[currSchedule];
    const currentAgeIndex = ageGroups.findIndex(
        (age) => hoursFromBirth < parseInt(age.minAgeFromBirth),
    );
    const currAgeGroup = currentAgeIndex === -1 ? ageGroups.length : currentAgeIndex;

    const onSaveChanges = () => {
        onSave?.((loading) => setIsSaving(loading));
    };

    const dropdowns = [
        {
            key: VACCINATION_STATUS.DUE,
            text: VACCINATION_STATUS.DUE,
            type: 'dropdown',
            options: [
                {
                    key: 'next',
                    text: 'Next vaccines as due',
                },
            ],
        },
        {
            key: VACCINATION_STATUS.GIVEN,
            text: VACCINATION_STATUS.GIVEN,
            type: 'dropdown',
            options: [
                {
                    key: 'all',
                    text: 'All past vaccines as given',
                },
                {
                    key: 'current',
                    text: `Current vaccines as given`,
                },
            ],
        },
    ];

    const dropDownFinal =
        currAgeGroup === ageGroups.length
            ? dropdowns.filter((d) => d.key !== VACCINATION_STATUS.DUE)
            : dropdowns;

    const switchVaccSchedule = (schedule: 'plan' | 'otherVaccs') => {
        setCurrSchedule(schedule);
        setVaccineData(vaccineData);
        setAgeGroups(vaccineData.columns ? vaccineData.columns[schedule] : []);

        const keys = Object.keys(vaccineData ? vaccineData[schedule] : {});

        setCategoryKeys({
            allKeys: keys,
            currKeys: keys,
        });
    };

    const patientFuse = useMemo(
        () =>
            new Fuse(
                Object.keys(vaccineData[currSchedule as keyof typeof vaccineData] ?? {}).map(
                    (t) => ({
                        _category: diseaseIdToName[t].name,
                        _id: t,
                    }),
                ),
                fuseConfig,
            ),
        [vaccineData, currSchedule],
    );

    const { finalVals } = formVaccineData(
        vaccineData,
        categoryKeys,
        ageGroups,
        selectedVaccines,
        (val: Dose[]) => setSelectedVaccines(val),
        (val: { isVisible: boolean; status?: VACCINATION_STATUS.GIVEN | VACCINATION_STATUS.DUE }) =>
            setShowModal({ ...val, isBlockStatus: false }),
        administedVaccines,
        currSchedule,
        dob,
        today,
    );

    const onLeftClick = () => {
        const container = tableRefCurr.current?.querySelector('.ant-table-body');

        if (container) {
            sideScroll(container, -1 * doseBoxWidth);
        }
    };

    const onRightClick = () => {
        const container = tableRefCurr.current?.querySelector('.ant-table-body');

        if (container) {
            sideScroll(container, doseBoxWidth);
        }
    };

    const selectVaccines = (
        status: VACCINATION_STATUS.GIVEN | VACCINATION_STATUS.DUE,
        key: string,
    ) => {
        let ageGs: (Column & {
            checked?: boolean | undefined;
        })[] = [];

        if (key === 'next') {
            const groups = ageGroups.filter((a) => parseInt(a.minAgeFromBirth) > hoursFromBirth);
            ageGs = [...groups].sort().slice(0, 1);
        } else {
            if (key === 'all') {
                ageGs = ageGroups.filter((a) => parseInt(a.minAgeFromBirth) < hoursFromBirth);
            }

            if (key === 'current') {
                const groups = ageGroups.filter(
                    (a) => parseInt(a.minAgeFromBirth) <= hoursFromBirth,
                );
                ageGs = [...groups]
                    .sort((a, b) => parseInt(a.minAgeFromBirth) - parseInt(b.minAgeFromBirth))
                    .slice(-1);
            }
        }

        let allVacs: Dose[] = [];

        for (let i = 0; i < categoryKeys.currKeys.length; i++) {
            const v = categoryKeys.currKeys[i];
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const currVac = (vaccineData[currSchedule] ?? {})[v].vaccineSchedule;

            for (let j = 0; j < ageGs.length; j++) {
                const ageG = ageGs[j];

                if (ageG.ageGroupName && currVac[ageG.ageGroupName]) {
                    const currVal = currVac[ageG.ageGroupName];

                    allVacs.push(...currVal);
                }
            }
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        allVacs = allVacs.map((v) => {
            const curr = administedVaccines.find((val) => val.id === v.vaccineId);

            return curr
                ? {
                      ...v,
                      status: curr,
                  }
                : {
                      ...v,
                      status: {
                          ...v.status,
                          date:
                              key === 'next'
                                  ? dob
                                        .clone()
                                        .add(parseInt(v.dateRange.minAgeFromBirth), 'hours')
                                        .toISOString()
                                  : key === 'all'
                                  ? undefined
                                  : today.toISOString(),
                      },
                  };
        });

        const ageGNames = ageGs.map((a) => a.ageGroupName);
        setSelectedVaccines(allVacs);
        setAgeGroups((prev) =>
            prev.map((p) => (ageGNames.includes(p.ageGroupName) ? { ...p, checked: true } : p)),
        );
        setShowModal((prev) => ({ ...prev, isVisible: true, status }));
    };

    const clearSelections = () => {
        clearSelection?.();
    };

    const setAgeGroupChecked = (checked: boolean, ageGroup?: string) => {
        if (ageGroup) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            setAgeGroups((prev) =>
                prev.map((p) => (p.ageGroupName === ageGroup ? { ...p, checked } : p)),
            );

            setSelectedVaccines((prev) =>
                prev ? prev.filter((p) => p.dateRange.ageGroupName !== ageGroup) : [],
            );
        } else {
            setAgeGroups((prev) => prev.map((p) => ({ ...p, checked: false })));
            setSelectedVaccines([]);
        }
    };

    const selectBirthBGroup = (ageGroup: string) => {
        setAgeGroupChecked(true, ageGroup);

        const selectedVaccs: Dose[] = [];

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (vaccineData[currSchedule]) {
            for (let i = 0; i < categoryKeys.currKeys.length; i++) {
                const key = categoryKeys.currKeys[i];
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const currSch = vaccineData[currSchedule][key].vaccineSchedule[ageGroup];

                if (currSch) selectedVaccs.push(...currSch);
            }
        }

        setSelectedVaccines((prev) => [...(prev ?? []), ...selectedVaccs]);
    };

    useEffect(() => {
        const container = tableRefCurr.current?.querySelector('.ant-table-body');

        if (container && currSchedule === 'plan') {
            sideScroll(container, (currAgeGroup ? currAgeGroup - 1 : 0) * 190);
        }

        patientId &&
            getPatientByLocalId(patientId).then((val) => {
                setPatientDob(val.personal.age?.dob);
                setAge(val.personal.age);
            });
    }, [currAgeGroup, patientId]);

    const ageString = age
        ? getAgeString(
              {
                  age: age?.value,
                  month: age?.month,
                  dob: age?.dob,
              },
              undefined,
              '',
          )
        : '';

    const gotoMyRXTemplate = () => history.push(GO_TO_MY_RX_TEMPLATE);

    // const cid = px?.clinic._id ?? getDefaultClinicId() ?? '';
    const cid = clinic_id;

    const showBanner =
        (!getDefaultTemplateV2(cid, 'PRINT') && getTemplates()?.print) ||
        (!getDefaultTemplateV2(cid, 'SEND') && getTemplates()?.send);

    return (
        <div className="flex hide-scrollbar flex-col bg-darwin-neutral_new-50 h-full w-full">
            <div
                className={`flex items-center justify-between w-full py-8 px-16 ${
                    selectedVaccines && selectedVaccines.length !== 0
                        ? 'bg-darwin-accent-bright-blue-800 text-white'
                        : ''
                }`}
            >
                <div className="flex items-center gap-16">
                    <Dropdown
                        trigger={['click']}
                        overlay={
                            <Menu>
                                <Menu.Item onClick={() => switchVaccSchedule('plan')}>
                                    IAP Schedule
                                </Menu.Item>
                                <Menu.Item onClick={() => switchVaccSchedule('otherVaccs')}>
                                    Other vaccines
                                </Menu.Item>
                            </Menu>
                        }
                    >
                        <ButtonComp>
                            <p className="text-13 font-700">
                                {currSchedule === 'otherVaccs' ? 'Other Vaccines' : 'IAP Schedule'}
                            </p>
                            <span className="m-4">
                                {downArrowNew('w-10 h-6 fill-darwin-accent-bright-blue-800')}
                            </span>
                        </ButtonComp>
                    </Dropdown>
                    {selectedVaccines && selectedVaccines.length !== 0 ? (
                        <p className="text-14 font-700 text-white">
                            {selectedVaccines.length}{' '}
                            {selectedVaccines.length <= 1 ? 'dose' : 'doses'} selected
                        </p>
                    ) : (
                        <div className="flex items-center gap-8">
                            <p className="text-13">
                                Patient Age: <span className="font-700">{ageString}</span>
                            </p>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-8">
                    <p className="text-13 font-400">Update:</p>
                    <div className="flex items-center gap-8">
                        {selectedVaccines && selectedVaccines.length !== 0
                            ? buttons.map((b) =>
                                  b.text !== 'Reset' ? (
                                      <ButtonComp
                                          onClick={() => {
                                              if (b.text === VACCINATION_STATUS.GIVEN) {
                                                  setShowModal({
                                                      status: VACCINATION_STATUS.GIVEN,
                                                      isVisible: true,
                                                      isBlockStatus: false,
                                                  });
                                              } else if (b.text === VACCINATION_STATUS.DUE) {
                                                  setShowModal({
                                                      status: VACCINATION_STATUS.DUE,
                                                      isVisible: true,
                                                      isBlockStatus: false,
                                                  });
                                              } else if (
                                                  b.text === VACCINATION_STATUS.PATIENT_REFUSED ||
                                                  b.text === VACCINATION_STATUS.MISSED
                                              ) {
                                                  setShowModal({
                                                      status: b.text,
                                                      isVisible: true,
                                                      isBlockStatus: false,
                                                  });
                                              }
                                          }}
                                      >
                                          {b.icon2}
                                          {b.text && !b.hideText && (
                                              <p className="text-13 font-700">{b.text}</p>
                                          )}
                                      </ButtonComp>
                                  ) : (
                                      <span
                                          className="cursor-pointer ml-4"
                                          onClick={() => {
                                              setAgeGroupChecked(false);
                                              setSelectedVaccines([]);
                                          }}
                                      >
                                          {b.icon2}
                                      </span>
                                  ),
                              )
                            : dropDownFinal.map((d) =>
                                  d.options.map((opt) => (
                                      <ButtonComp
                                          key={opt.key}
                                          onClick={() =>
                                              selectVaccines(
                                                  d.key as
                                                      | VACCINATION_STATUS.GIVEN
                                                      | VACCINATION_STATUS.DUE,
                                                  opt.key,
                                              )
                                          }
                                      >
                                          {opt.text}
                                      </ButtonComp>
                                  )),
                              )}
                    </div>
                </div>
            </div>
            {currPlan && categoryKeys.allKeys.length !== 0 && ageGroups.length !== 0 ? (
                <Fragment>
                    <div
                        onClick={onLeftClick}
                        style={{ top: '50%' }}
                        className="flex z-10 absolute left-210 top-0 cursor-pointer justify-center items-center w-28 h-28 bg-darwin-neutral-400 hover:bg-darwin-neutral-900 rounded-4"
                    >
                        {leftScrollButton()}
                    </div>
                    <div
                        onClick={onRightClick}
                        style={{ top: '50%' }}
                        className="absolute z-10 right-24 top-0 flex cursor-pointer justify-center items-center w-28 h-28 bg-darwin-neutral-400 hover:bg-darwin-neutral-900 rounded-4"
                    >
                        {rightScrollButton()}
                    </div>
                    <Table
                        bordered
                        sticky={true}
                        pagination={false}
                        columns={[
                            {
                                title: (
                                    <div
                                        className="h-full border-b border-darwin-neutral_new-200 w-full p-16"
                                        style={{
                                            boxShadow: '0px 0px 10px #D1D1D1',
                                            clipPath: 'inset(0px -15px 0px 0px)',
                                        }}
                                    >
                                        <Input
                                            onChange={(e) => {
                                                const val = patientFuse.search(e.target.value);

                                                if (!e.target.value) {
                                                    setCategoryKeys((prev) => {
                                                        return {
                                                            ...prev,
                                                            currKeys: categoryKeys.allKeys,
                                                        };
                                                    });
                                                } else {
                                                    setCategoryKeys((prev) => {
                                                        return {
                                                            ...prev,
                                                            currKeys: val.map((v) => v.item._id),
                                                        };
                                                    });
                                                }
                                            }}
                                            className="rounded-8"
                                            placeholder="Search"
                                            size="large"
                                            prefix={searchIcon('w-14 h-14 mx-4')}
                                        />
                                    </div>
                                ),
                                dataIndex: 'Disease',
                                key: 'Disease',
                                width: doseBoxWidth,
                                fixed: true,
                                className: 'dose-cell',
                            },
                            ...ageGroups.map((c, idx) => ({
                                title:
                                    idx === currAgeGroup - 1 && currSchedule === 'plan' ? (
                                        <Badge.Ribbon
                                            text={
                                                <p className="text-11 pt-2 pr-2 font-700 text-white">
                                                    Current Age Group
                                                </p>
                                            }
                                            className="top-0"
                                            style={{ borderRadius: '0 0 0 4px' }}
                                        >
                                            <div className="flex py-24 px-16 border-b border-darwin-neutral_new-200 select-none items-center h-full w-full justify-between bg-darwin-accent-bright-blue-100">
                                                <p className="text-14 font-700">
                                                    {c.ageGroupName ?? ''}
                                                </p>
                                                <Checkbox
                                                    onChange={(e) => {
                                                        if (e.target.checked === true) {
                                                            selectBirthBGroup(c.ageGroupName ?? '');
                                                        } else {
                                                            setAgeGroupChecked(
                                                                false,
                                                                c.ageGroupName ?? '',
                                                            );
                                                        }
                                                    }}
                                                    checked={!!c.checked}
                                                />
                                            </div>
                                        </Badge.Ribbon>
                                    ) : (
                                        <div className="flex py-24 px-16 border-b border-darwin-neutral_new-200 select-none items-center h-full w-full justify-between bg-white">
                                            <p className="text-14 font-700">
                                                {c.ageGroupName ?? ''}
                                            </p>
                                            <Checkbox
                                                onChange={(e) => {
                                                    if (e.target.checked === true) {
                                                        selectBirthBGroup(c.ageGroupName ?? '');
                                                    } else {
                                                        setAgeGroupChecked(
                                                            false,
                                                            c.ageGroupName ?? '',
                                                        );
                                                    }
                                                }}
                                                checked={!!c.checked}
                                            />
                                        </div>
                                    ),
                                dataIndex: c.ageGroupName,
                                key: c.ageGroupName,
                                width: doseBoxWidth,
                                className: 'dose-cell',
                            })),
                        ]}
                        dataSource={finalVals}
                        scroll={{ x: 740 }}
                        ref={tableRefCurr}
                        key={currSchedule}
                        rowClassName="h-100 max-h-100"
                        className="flex flex-1 flex-col overflow-y-scroll h-full"
                    />
                </Fragment>
            ) : null}

            <VaccineSelectionModal
                isVisible={!!(showModal.isVisible && selectedVaccines?.length)}
                closeModal={() =>
                    setShowModal((prev) => ({ ...prev, isVisible: false, isBlockStatus: false }))
                }
                selectedVaccines={selectedVaccines}
                setSelectedVaccines={(val: Dose[] | undefined) => setSelectedVaccines(val)}
                setAdministedVaccines={(val: IVaccineStatus[]) => setAdministedVaccines(val)}
                initialStatus={
                    selectedVaccines
                        ? selectedVaccines.map((vac) => {
                              return {
                                  id: vac.vaccineId,
                                  status: vac.status?.status ?? showModal.status,
                                  batchNumber: vac.status?.batchNumber,
                                  brand: vac.status?.brand,
                                  date: vac.status?.date ?? showModal.date,
                                  notes: vac.status?.notes,
                                  diseaseId: vac.diseaseId,
                                  ageGroup: vac.dateRange.ageGroupName,
                                  doseNo: vac.doseNo,
                                  name: vac.vaccineName,
                              };
                          })
                        : []
                }
                vaccineData={currPlan}
                administeredVaccines={administedVaccines}
                dob={dob}
                clearCheckedAgeGroups={() =>
                    setAgeGroups((prev) => prev.map((p) => ({ ...p, checked: false })))
                }
                currPlan={currPlan}
                title={showModal.title}
                isBlockStatus={showModal.isBlockStatus}
                onChange={onChange}
                onDueFollowupSet={onDueFollowupSet}
            />

            {showBanner && (
                <div
                    className="flex items-center p-8 justify-center space-x-8 w-300 py-10 absolute right-16 bottom-92 border rounded-8 border border-darwin-status-warning-500 bg-darwin-status-warning-100 cursor-pointer"
                    onClick={gotoMyRXTemplate}
                >
                    <div className="font-700 text-darwin-status-warning-500">
                        Click to configure your RX template for vaccination printing.
                    </div>
                </div>
            )}

            <PadBottomStrip
                clearSelections={clearSelections}
                openFollowUp={() => {
                    setShowModal((prev) => ({
                        ...prev,
                        title: 'Upcoming vaccinations',
                        isBlockStatus: true,
                    }));

                    if (currAgeGroup !== ageGroups.length) {
                        selectVaccines(VACCINATION_STATUS.DUE, 'next');
                    } else {
                        selectVaccines(VACCINATION_STATUS.DUE, 'current');
                    }
                }}
                currSchedule={currSchedule}
                loading={isSaving}
                onSave={onSave ? onSaveChanges : undefined}
            />

            <style>
                {`
                    .ant-table-thead {
                        .ant-table-cell {
                            background: white;
                            font-weight: 700;
                        }
                    } 
                    
                    .dose-cell {
                        padding: 0 !important;
                        height: 0;
                        border-color: #D1D1D1 !important;
                    }

                    .ant-ribbon-wrapper {
                        height: 100%;
                    }
                `}
            </style>
        </div>
    );
};

export default VaccinationChart;

export const ButtonComp: React.FC<{ onClick?: () => void }> = ({ children, onClick }) => (
    <div
        onClick={onClick}
        className="flex text-13 self-stretch font-700 cursor-pointer rounded-4 px-6 py-4 active:bg-darwin-accent-bright-blue-100 hover:bg-darwin-accent-bright-blue-100 gap-4 items-center justify-center bg-white border border-darwin-accent-bright-blue-800 text-darwin-accent-bright-blue-800"
    >
        {children}
    </div>
);
